import { useCallback, useEffect, useRef, useState } from 'react';

import { InteractionManager, Platform, ScrollView, View } from 'react-native';

import { scrollToTargetInScrollView } from 'utils/scroll/scroll-to-target-in-scroll-view';

const DEFAULT_SCROLL_OFFSET = 200;

interface UseScrollToCustomizationOptions {
  /** Scroll container that wraps the combo slot list and inline customization block. */
  scrollViewRef: React.RefObject<ScrollView | null>;
  /**
   * Value that should trigger a scroll when it changes after mount, e.g. the
   * selected combo slot item id.
   */
  scrollTrigger: unknown;
  /** When false, the hook only exposes refs and never scrolls. Defaults to true. */
  enabled?: boolean;
  /**
   * Duo/trio flows with multiple combo slot cards. On Android, defers mounting
   * the inline customization block until interactions settle and adjusts scroll
   * behavior to avoid layout issues during the product-detail slide-in.
   */
  hasMultipleComboSlotOptions?: boolean;
  /** Pixels of content to leave visible above the customization block. Defaults to 200. */
  offset?: number;
  /** Whether to animate the scroll. Defaults to false on Android. */
  animated?: boolean;
}

interface UseScrollToCustomizationResult {
  /** Attach to the inline customization wrapper `View`. */
  customizationRef: React.RefObject<View | null>;
  /** Attach to the same wrapper via `onLayout` to retry scroll after layout. */
  handleCustomizationLayout: () => void;
  /** Imperative scroll helper; returns false when refs are not ready. */
  scrollToCustomization: () => boolean;
  /** When false, inline customization should stay collapsed (Android multi-slot open). */
  canShowInlineCustomization: boolean;
  /** Call after the user selects a combo slot item (e.g. in onProductPress). */
  onUserComboSlotSelection: () => void;
}

/**
 * Scrolls a product customization screen so an inline modifier block is visible
 * after the user changes combo slot selection.
 *
 * Used by the modifier redesign experiment on v1 and v2 picker-aspect flows when
 * multiple combo slot options render customization inline beneath the selected row.
 *
 * Behavior:
 * - Records the baseline trigger the first time scrolling is enabled and only
 *   scrolls when `scrollTrigger` changes after that (user interaction).
 * - On Android with multiple combo slot options, defers `canShowInlineCustomization`
 *   until interactions settle, skips `onLayout`-driven scroll, and scrolls on
 *   `onUserComboSlotSelection` instead.
 *
 * @example
 * ```tsx
 * const {
 *   customizationRef,
 *   handleCustomizationLayout,
 *   canShowInlineCustomization,
 *   onUserComboSlotSelection,
 * } = useScrollToCustomization({
 *   scrollViewRef,
 *   scrollTrigger: selectedItemId,
 *   enabled: hasInlineCustomization,
 *   hasMultipleComboSlotOptions: comboSlotItems.length > 1,
 * });
 * ```
 */
export function useScrollToCustomization({
  scrollViewRef,
  scrollTrigger,
  enabled = true,
  hasMultipleComboSlotOptions = false,
  animated = Platform.OS !== 'android',
  offset = DEFAULT_SCROLL_OFFSET,
}: UseScrollToCustomizationOptions): UseScrollToCustomizationResult {
  const isAndroidMultipleComboSlotOptions =
    Platform.OS === 'android' && hasMultipleComboSlotOptions;
  const [canShowInlineCustomization, setCanShowInlineCustomization] = useState(
    !isAndroidMultipleComboSlotOptions
  );

  const customizationRef = useRef<View>(null);
  const pendingScrollToCustomizationRef = useRef(false);
  const previousScrollTriggerRef = useRef<unknown>(undefined);
  const wasEnabledRef = useRef(false);

  useEffect(() => {
    if (!isAndroidMultipleComboSlotOptions) {
      return;
    }

    const task = InteractionManager.runAfterInteractions(() => {
      setCanShowInlineCustomization(true);
    });

    return () => task.cancel?.();
  }, [isAndroidMultipleComboSlotOptions]);

  const scrollToCustomization = useCallback(() => {
    const scrollView = scrollViewRef.current;
    const target = customizationRef.current;

    if (!scrollView || !target) {
      return false;
    }

    scrollToTargetInScrollView(scrollView, target, {
      animated,
      offset,
    });

    return true;
  }, [animated, offset, scrollViewRef]);

  const scheduleScrollToCustomization = useCallback(() => {
    pendingScrollToCustomizationRef.current = true;

    const task = InteractionManager.runAfterInteractions(() => {
      requestAnimationFrame(() => {
        if (scrollToCustomization()) {
          pendingScrollToCustomizationRef.current = false;
        }
      });
    });

    return () => {
      task.cancel?.();
    };
  }, [scrollToCustomization]);

  const handleCustomizationLayout = useCallback(() => {
    if (isAndroidMultipleComboSlotOptions || !pendingScrollToCustomizationRef.current) {
      return;
    }

    if (scrollToCustomization()) {
      pendingScrollToCustomizationRef.current = false;
    }
  }, [isAndroidMultipleComboSlotOptions, scrollToCustomization]);

  const shouldAutoScroll =
    enabled && canShowInlineCustomization && !isAndroidMultipleComboSlotOptions;

  useEffect(() => {
    if (!shouldAutoScroll) {
      wasEnabledRef.current = false;
      return;
    }

    if (!wasEnabledRef.current) {
      wasEnabledRef.current = true;
      previousScrollTriggerRef.current = scrollTrigger;
      return;
    }

    if (Object.is(previousScrollTriggerRef.current, scrollTrigger)) {
      return;
    }

    previousScrollTriggerRef.current = scrollTrigger;

    return scheduleScrollToCustomization();
  }, [shouldAutoScroll, scrollTrigger, scheduleScrollToCustomization]);

  const onUserComboSlotSelection = useCallback(() => {
    if (!isAndroidMultipleComboSlotOptions) {
      return;
    }

    setCanShowInlineCustomization(true);
    scheduleScrollToCustomization();
  }, [isAndroidMultipleComboSlotOptions, scheduleScrollToCustomization]);

  return {
    customizationRef,
    handleCustomizationLayout,
    scrollToCustomization,
    canShowInlineCustomization,
    onUserComboSlotSelection,
  };
}
